import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import {
  createBootingAuthSession,
  createGuestAuthSession,
  type AuthActor,
  type AuthUser,
  type OnboardingServerStatus,
} from "@/features/auth/authSessionSchema";

interface AuthenticatedSessionPayload {
  actor: AuthActor;
  onboardingRevision: number;
  onboardingStatus: OnboardingServerStatus;
  user: AuthUser;
}

const authSessionSlice = createSlice({
  initialState: createBootingAuthSession(),
  name: "authSession",
  reducers: {
    authenticatedSessionEstablished: (
      state,
      action: PayloadAction<AuthenticatedSessionPayload>,
    ) => ({
      ...state,
      ...action.payload,
      status: "authenticated" as const,
    }),
    clearAuthenticatedSession: () => createGuestAuthSession(),
    markOnboardingCompleted: (state, action: PayloadAction<number | undefined>) => {
      if (state.status === "authenticated") {
        state.onboardingStatus = "completed";
        state.onboardingRevision = action.payload ?? state.onboardingRevision;
      }
    },
    markWelcomeMotionSeen: (state) => {
      if (state.status === "authenticated") {
        state.welcomeMotionSeen = true;
      }
    },
    onboardingDraftSaved: (state, action: PayloadAction<number>) => {
      if (state.status === "authenticated") {
        state.onboardingRevision = action.payload;
        state.onboardingStatus = "draft";
      }
    },
    sessionBooting: () => createBootingAuthSession(),
    sessionExpired: () => createGuestAuthSession(),
  },
});

export const {
  authenticatedSessionEstablished,
  clearAuthenticatedSession,
  markOnboardingCompleted,
  markWelcomeMotionSeen,
  onboardingDraftSaved,
  sessionBooting,
  sessionExpired,
} = authSessionSlice.actions;

export const selectAuthSessionStatus = (state: RootState) =>
  state.authSession.status;
export const selectIsAuthenticated = (state: RootState) =>
  state.authSession.status === "authenticated";
export const selectWelcomeMotionSeen = (state: RootState) =>
  state.authSession.welcomeMotionSeen;
export const selectOnboardingCompleted = (state: RootState) =>
  state.authSession.onboardingStatus === "completed";
export const selectOnboardingServerStatus = (state: RootState) =>
  state.authSession.onboardingStatus;
export const selectAuthActor = (state: RootState) => state.authSession.actor;

export default authSessionSlice.reducer;
